// React import no longer needed with the new JSX runtime
import { RoleType } from "@promo/shared";
import { Table, TableBody, TableCell, TableRow } from "@/components/ui/table";
import { Badge } from "@/components/ui/badge";
import { useInvitations } from "../hooks/useInvitations";
import { parseNameFromEmail } from '@promo/shared';
import { Skeleton } from "@/components/ui/skeleton";

interface InvitationsListProps {
  packetId: string;
  role: RoleType;
}

export function InvitationsList({ packetId, role }: InvitationsListProps) {
  const { data: invitations, isLoading, error } = useInvitations(packetId, role);

  if (isLoading) {
    return <div className="text-sm text-muted-foreground">Loading invitations...</div>;
  }

  if (error) {
    return <div className="text-sm text-destructive">Failed to load invitations</div>;
  }

  if (!invitations || invitations.length === 0) {
    return (
      <div className="mt-2 mb-4">
        <Table>
          <TableBody>
            {[...Array(3)].map((_, idx) => (
              <TableRow key={idx}>
                <TableCell><Skeleton className="h-4 w-32" /></TableCell>
                <TableCell><Skeleton className="h-4 w-20" /></TableCell>
                <TableCell><Skeleton className="h-4 w-24" /></TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>
    );
  }

  return (
    <div className="mt-2 mb-4">
      <Table>
        <TableBody>
          {invitations.map((invitation) => (
            <TableRow key={invitation.id}>
              <TableCell>
                {/* Always show parsed name if available, otherwise email */}
                {parseNameFromEmail(invitation.inviteeEmail) || invitation.inviteeEmail}
              </TableCell>
              <TableCell>
                <StatusBadge status={invitation.status} />
              </TableCell>
              <TableCell className="text-sm text-muted-foreground">
                {formatDate(invitation.expiresAt)}
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </div>
  );
}

function StatusBadge({ status }: { status: string }) {
  let variant: "default" | "secondary" | "outline" | "destructive" = "default";
  
  switch (status) {
    case "PENDING":
      variant = "secondary";
      break;
    case "ACCEPTED":
      variant = "default";
      break;
    case "DECLINED":
      variant = "destructive";
      break;
    case "EXPIRED":
      variant = "outline";
      break;
  }
  
  return <Badge variant={variant}>{status.toLowerCase()}</Badge>;
}

function formatDate(date: string | Date) {
  if (!date) return "";
  
  const d = typeof date === "string" ? new Date(date) : date;
  return d.toLocaleDateString(undefined, { month: 'long', day: 'numeric' });
} 